import clsx from 'clsx';
import * as React from 'react';

import { Tooltip } from '../Tooltip';
import styles from './Avatar.module.css';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg';

// For elements that are positioned absolute over an avatar, the size of the badge should be 2/3 of the avatar size
export const badgeOverAvatarSize = {
    xs: 9,
    sm: 16,
    md: 21,
    lg: 32,
};

export interface AvatarProps {
    /**
     * The display name of the user
     */
    displayName: string;

    /**
     * The user avatar image URL
     */
    photoURL?: string | null;

    /**
     * For users without an avatar, use these colors
     */
    fallbackColors: { background: string; text: string };

    /**
     * The size of the avatar. A named preset (`xs`/`sm`/`md`/`lg`, 16/24/32/48px) or an exact pixel
     * number (e.g. `14`, `40`) — a number renders at precisely that dimension, scaling the initials
     * and border with it.
     */
    size?: AvatarSize | number;

    /** Optional styling for the avatar container */
    className?: string;

    /** Show a tooltip with the the user's name
     * @default true
     */
    tooltip?: boolean;

    /** Content of the tooltip */
    tooltipLabel?: string;

    /** Prepend a given string before the username */
    tooltipPrefix?: string;

    /**
     * Draw a subtle 1px `var(--neutral-4)` ring, e.g. to separate a light photo from a light
     * surface. Ignored when `borderColor` is set.
     * @default false
     */
    bordered?: boolean;

    /** Ring colour, e.g. live multiplayer identity colours or a stack's surface colour. */
    borderColor?: string;

    /** Whether to use a squared avatar */
    squared?: boolean;
}

const avatarSizes = {
    xs: {
        dimensions: styles.xs,
        // Optical nudge: at this size the trimmed capitals still read a pixel high. Only where
        // `text-box` trims; untrimmed initials would be pushed off-centre instead.
        text: 'text-[0.5rem] supports-[text-box:trim-both_cap_alphabetic]:mt-px',
        borderWidth: 1,
    },
    sm: {
        dimensions: styles.sm,
        text: 'text-ui-small',
        borderWidth: 2,
    },
    md: {
        dimensions: styles.md,
        text: 'text-ui-base',
        borderWidth: 2,
    },
    lg: {
        dimensions: styles.lg,
        // Optical nudge: at this size the trimmed capitals still read a pixel low (with `text-box`).
        text: 'text-ui-heading-medium supports-[text-box:trim-both_cap_alphabetic]:-mt-px',
        borderWidth: 2,
    },
};

/** Pixel dimension each avatar renders at — mirrors Avatar.module.css. Used to right-size fetched avatar images. */
export const avatarPixelSize: Record<AvatarSize, number> = { xs: 16, sm: 24, md: 32, lg: 48 };

/**
 * Resolve an avatar `size` prop to the pixel width it renders at, so image requests match the
 * display size. Fractional sizes round up, so the fetched image is never smaller than its box.
 */
export function resolveAvatarPixelSize(size: AvatarSize | number | undefined): number {
    if (typeof size === 'number') {
        return normalizeAvatarPixelSize(size);
    }
    return avatarPixelSize[size ?? 'md'];
}

/**
 * Resolve avatar size to set dimensions for avatar and text inside.
 *
 * Named sizes map to fixed CSS classes. A numeric (or missing) size uses the "flexible" path: the box
 * is `1em` and the initials `0.5em` (as `xs`), so the returned `fontSize` — applied inline on the container —
 * makes `1em` equal that many pixels and cascades to the image, fallback and initials. That is how a
 * numeric `size` renders at an exact pixel dimension instead of snapping to a preset.
 */
export function resolveAvatarSize(size: AvatarSize | number | undefined) {
    const flexible = {
        dimensions: 'size-[1em] min-w-[1em]',
        text: 'text-[0.5em]',
        borderWidth: 2,
        fontSize: undefined as number | undefined,
    };
    if (typeof size === 'number') {
        const pixelSize = normalizeAvatarPixelSize(size);
        return { ...flexible, borderWidth: pixelSize <= 16 ? 1 : 2, fontSize: pixelSize };
    }
    if (!size) {
        return flexible;
    }
    return { ...avatarSizes[size], fontSize: undefined as number | undefined };
}

/**
 * Avatar for a user.
 */
export function Avatar({ ref, ...props }: AvatarProps & { ref?: React.Ref<HTMLDivElement> }) {
    const {
        displayName,
        photoURL,
        className,
        tooltip = true,
        borderColor,
        bordered = false,
        squared = false,
        fallbackColors,
        tooltipPrefix,
        tooltipLabel = `${tooltipPrefix ? `${tooltipPrefix} ` : ''}${displayName}`,
    } = props;

    const size = resolveAvatarSize(props.size);

    // Squared avatars are only used for images and when explicitly requested
    const hasSquaredBorder = squared && photoURL;

    const border = borderColor
        ? { borderColor, borderWidth: size.borderWidth, borderStyle: 'solid' as const }
        : bordered
          ? { borderColor: 'var(--neutral-4)', borderWidth: 1, borderStyle: 'solid' as const }
          : null;

    const anonymousAvatar = {
        ...(!photoURL
            ? {
                  backgroundColor: fallbackColors?.background,
                  color: fallbackColors?.text,
              }
            : null),
    };

    return (
        <Tooltip disabled={!tooltip} label={tooltipLabel}>
            <div
                ref={ref}
                className={clsx(
                    styles.avatar,
                    hasSquaredBorder ? styles.squared : null,
                    size.dimensions,
                    className
                )}
                // A numeric size sets font-size here so the `1em`-based dimensions/text resolve to it
                // and cascade to the image, fallback and initials below.
                style={{ ...border, fontSize: size.fontSize }}
            >
                {photoURL ? (
                    // Avatar
                    <img
                        src={photoURL}
                        className={clsx(
                            styles.image,
                            hasSquaredBorder ? styles.squared : null,
                            'size-full min-w-0'
                        )}
                        aria-label={tooltipLabel}
                        alt={displayName}
                    />
                ) : (
                    // No avatar
                    <div
                        role="image"
                        aria-label={tooltipLabel}
                        className={clsx(styles.avatar, 'size-full min-w-0')}
                        style={anonymousAvatar}
                    >
                        {/* Trimming the text box to the capitals (initials are uppercase) centres them
                            by their visible height, not the font's ascent/descent, which small sizes
                            otherwise round unevenly across platforms. */}
                        <span
                            className={clsx(
                                'leading-none font-medium [text-box:trim-both_cap_alphabetic]',
                                size.text
                            )}
                        >
                            {getInitials(displayName)}
                        </span>
                    </div>
                )}
            </div>
        </Tooltip>
    );
}

/** Normalize exact numeric sizes once so CSS dimensions and fetched image sizes cannot diverge. */
function normalizeAvatarPixelSize(size: number): number {
    return Number.isFinite(size) ? Math.max(1, Math.ceil(size)) : avatarPixelSize.md;
}

// If the displayName is a single uninterrupted string, use the first 2 chars. If there are space-separated
// words, use the first char of the first two words.
function getInitials(displayName: string) {
    const words = displayName.trim().split(/\s+/);

    if (words.length > 1) {
        return (words[0][0] + words[1][0]).toUpperCase();
    }
    return displayName.substring(0, 2).toUpperCase();
}
